import styled from "@emotion/styled";
import InnoIcons from "@InnoIcons/";
import theme from "@theme/";

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

// Dropdown button
const DropdownButton = styled.button`
  background: white;
  border: 1px solid #e5e7eb;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  // min-width: 180px;
  min-width: ${(props) => props.minWidth || "180px"};

  justify-content: space-between;
  transition: all 0.2s ease;

  /* subtle shadow by default */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15); /* slightly stronger on hover */
  }

  /* Open state */
  ${(props) =>
    props.isOpen &&
    `
    border-color: ${theme.colors.themeColor};
    box-shadow: 0 0 0 2px ${theme.colors.themeColor}, 0 4px 12px rgba(0,0,0,0.15);
  `}

  /* Active state (filter applied) */
  ${(props) =>
    props.isActive &&
    `
    border-color: ${theme.colors.themeColor}; 
    background-color:${theme.colors.buttonbackgroundfilter}; /* subtle light orange */
    color: ${theme.colors.themeColor};
  `}
`;

const ChevronIcon = styled(InnoIcons.KeyboardArrowDownIcon)`
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  color: ${(props) =>
    props.isActive ? `${theme.colors.themeColor}` : "black"};
`;

const DropdownMenu = styled.div`
  padding: 8px 10px;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin-top: 4px;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  // min-width: 270px;
  min-width: ${(props) => props.minWidth || "270px"};
`;

const DropdownHeader = styled.div`
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f8f9fa;
  font-size: 12px;
  color: black;
  font-weight: 500;
  cursor: ${(props) => (props.isClickable ? "pointer" : "default")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s ease;
  position: relative; /* important so child absolute works */

  &:hover {
    background: ${(props) => (props.isClickable ? "#f1f3f4" : "#f8f9fa")};
  }
`;

const HeaderText = styled.span`
  flex: 1;
`;

const HeaderChevron = styled(InnoIcons.KeyboardArrowRightIcon)`
  width: 20px;
  height: 20px;
  color: ${theme.colors.themeColor};
  transition: transform 0.2s ease;
  transform: ${(props) =>
    props.isExpanded ? "rotate(90deg)" : "rotate(0deg)"};
`;

const HeaderOptionsContainer = styled.div`
  display: ${(props) => (props.isVisible ? "block" : "none")};
  position: absolute;
  top: 15%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 150px;
  margin: 0px 10px;
`;

const HeaderOption = styled.div`
  padding: 8px 16px;
  font-size: 12px;
  cursor: pointer;
  background: ${(props) =>
    props.isSelected ? theme.gradient.backgroundTheme : "transparent"};
  color: ${(props) => (props.isSelected ? theme.colors.themeColor : "#374151")};
  transition: background-color 0.2s ease;
  position: relative;
  z-index: auto;
  &:hover {
    background: ${(props) =>
      props.isSelected ? theme.gradient.backgroundTheme : "#f9fafb"};
  }
`;

const SearchContainer = styled.div`
  padding: 8px 0px;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 6px 8px 6px 40px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 12px;
  background: #f8f9fa;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(InnoIcons.SearchIcon)`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #3b82f6;
`;

const OptionsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  padding: 4px 0;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const OptionItem = styled.label`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
  transition: background-color 0.1s ease;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const Checkbox = styled.input`
  margin-right: 8px;
  width: 14px;
  height: 14px;
  accent-color: #d97706;
`;

const DropdownFooter = styled.div`
  padding: 8px 12px;
  border-top: 1px solid #e5e7eb;
  text-align: right;
`;

const ClearAllButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f3f4f6;
    color: #374151;
  }
`;

const RemainingCount = styled.span`
  background-color: #f1980a; /* theme color */
  color: white; /* text contrast */
  font-weight: 600;
  margin-left: 4px;
  padding: 1px 4px;
  border-radius: 4px;
  font-size: 12px;
`;

const SC = {
  DropdownContainer,
  DropdownButton,
  HeaderChevron,
  SearchContainer,
  HeaderOptionsContainer,
  HeaderOption,
  SearchContainer,
  SearchInput,
  SearchIcon,
  OptionsList,
  OptionItem,
  Checkbox,
  DropdownFooter,
  ClearAllButton,
  DropdownMenu,
  DropdownHeader,
  HeaderText,
  ChevronIcon,
  RemainingCount,
};

export default SC;
